import { createContext, useContext } from 'react'

export type OnboardingStep = 1 | 2 | 3 | 4

export interface ColumnInfo {
  name: string
  inferred_type: 'numeric' | 'categorical' | 'boolean' | 'datetime' | 'text'
  sample_values: string[]
  null_percentage: number
  unique_count: number
}

export interface UseCaseSuggestion {
  use_case_name: string
  display_name: string
  confidence: number
  reason: string
  suggested_target: string
  suggested_features: string[]
  missing_columns: string[]
  is_trainable: boolean
}

export interface OnboardingState {
  currentStep: OnboardingStep
  isCompleted: boolean
  dataSourceId: string | null
  modelId: string | null
  predictionId: string | null
  useCase: string | null
  modelName: string
  columnInfo: ColumnInfo[] | null
  suggestedUseCases: UseCaseSuggestion[] | null
  suggestedTarget: string | null
}

export interface OnboardingContextType {
  state: OnboardingState
  setCurrentStep: (step: OnboardingStep) => void
  setDataSourceId: (id: string | null) => void
  setModelId: (id: string | null) => void
  setPredictionId: (id: string | null) => void
  setUseCase: (useCase: string | null) => void
  setModelName: (name: string) => void
  setColumnInfo: (info: ColumnInfo[] | null) => void
  setSuggestedUseCases: (suggestions: UseCaseSuggestion[] | null) => void
  setSuggestedTarget: (target: string | null) => void
  completeOnboarding: () => void
  resetOnboarding: () => void
  skipOnboarding: () => void
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export function useOnboardingContext() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider')
  }
  return context
}

export { OnboardingContext }
